import { motion } from "framer-motion";
import { MessageSquare, Users, Zap, BarChart3, ArrowRight } from "lucide-react";

const steps = [
  {
    number: 1,
    icon: MessageSquare,
    title: "Schedule Consultation",
    description: "Tell us about your support needs, volume, and requirements.",
    details: [
      "30-minute discovery call",
      "Define team size & skills needed",
      "Review your current processes",
    ],
    duration: "Day 1",
  },
  {
    number: 2,
    icon: Users,
    title: "We Build Your Team",
    description:
      "Our specialists recruit, vet, and train your dedicated support team.",
    details: [
      "Recruit qualified agents",
      "Background verification",
      "Product & process training",
    ],
    duration: "Days 2-13",
  },
  {
    number: 3,
    icon: Zap,
    title: "Go Live",
    description:
      "Your team goes live with full CRM integration and monitoring.",
    details: [
      "System setup & integration",
      "Quality assurance testing",
      "Soft launch with your team",
    ],
    duration: "Day 14",
  },
  {
    number: 4,
    icon: BarChart3,
    title: "Monitor & Optimize",
    description:
      "Real-time monitoring, analytics, and continuous improvement.",
    details: [
      "Daily performance tracking",
      "Weekly reports & insights",
      "Monthly strategy reviews",
    ],
    duration: "Ongoing",
  },
];

const CALENDLY_LINK = "https://calendly.com/pragma-consultation";

export default function HowItWorks() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section id="how-it-works" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent" />
      <div className="absolute top-1/2 right-0 w-96 h-96 border border-cyan-400/5 rounded-full translate-x-1/2" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            Simple Process
          </motion.span>
          <h2 className="text-foreground mb-6">
            How It <span className="text-cyan-400">Works</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            From consultation to live support in just 14 business days. Simple,
            transparent, and efficient.
          </p>
        </motion.div>

        {/* Steps */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative group"
                data-testid={`step-${index + 1}`}
              >
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 -right-2 w-4">
                    <ArrowRight className="w-4 h-4 text-cyan-400/30" />
                  </div>
                )}

                {/* Step Card */}
                <div className="relative border border-border p-6 bg-card/30 backdrop-blur-sm hover:border-cyan-400/50 transition-all duration-300 h-full group-hover:bg-card/50">
                  {/* Step Number */}
                  <motion.div
                    className="absolute -top-4 -left-4 w-12 h-12 bg-background border-2 border-cyan-400 flex items-center justify-center"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <span className="font-mono font-bold text-cyan-400 text-lg">
                      {step.number}
                    </span>
                  </motion.div>

                  {/* Duration Badge */}
                  <div className="absolute -top-3 right-4">
                    <span className="px-3 py-1 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-xs font-mono">
                      {step.duration}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className="mb-4 pt-6 text-cyan-400">
                    <Icon className="w-8 h-8" />
                  </div>

                  {/* Title */}
                  <h3 className="text-foreground font-mono font-bold text-lg mb-2 group-hover:text-cyan-400 transition-colors">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-muted-foreground text-sm mb-4">
                    {step.description}
                  </p>

                  {/* Details List */}
                  <ul className="space-y-2">
                    {step.details.map((detail, i) => (
                      <li
                        key={i}
                        className="text-muted-foreground/80 text-xs flex items-start gap-2"
                      >
                        <span className="text-cyan-400 mt-0.5">›</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Hover Accent */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-5 bg-gradient-to-br from-cyan-400 to-transparent pointer-events-none transition-opacity" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Timeline Summary */}
        <motion.div
          className="mt-16 p-8 border border-cyan-400/20 bg-gradient-to-r from-cyan-400/5 via-transparent to-pink-500/5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-foreground font-mono font-bold text-xl mb-2">
                Ready to get started?
              </h3>
              <p className="text-muted-foreground">
                Schedule your free consultation today and have your team live in
                14 business days.
              </p>
            </div>
            <motion.a
              href={CALENDLY_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="neon-button whitespace-nowrap group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="how-it-works-cta"
            >
              Schedule Consultation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
